import { useTranslation } from "react-i18next"

import { useMutation as useApolloMutation } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { MutationOptions } from "shared/types/MutationOptions"

import { useLazyProjects } from "entities/projects/api/useLazyProjects"

import {
  TeamInvitationFragment,
  TeamInvitationFragmentDoc
} from "../graphql/fragments.generated"
import {
  DeclineTeamInvitationDocument,
  DeclineTeamInvitationMutation,
  DeclineTeamInvitationMutationVariables
} from "../graphql/mutations.generated"
import { TeamInvitationsDocument } from "../graphql/queries.generated"
import { extractUuidsFromInvitationAcceptPath } from "../lib/extractUuidsFromInvitationAcceptPath"
import { TeamInvitation, TeamInvitationTypename } from "../types/TeamInvitation"

export function useDeclineTeamInvitation() {
  const { t } = useTranslation()
  const [apolloMutate] = useApolloMutation<
    DeclineTeamInvitationMutation,
    DeclineTeamInvitationMutationVariables
  >(DeclineTeamInvitationDocument)
  const { fetch: fetchProjects } = useLazyProjects()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      success: t("Request.TeamInvitation.DeclineTeamInvitation.Success"),
      failure: t("Request.TeamInvitation.DeclineTeamInvitation.Failure"),
      loading: t("Request.TeamInvitation.DeclineTeamInvitation.Start")
    }
  })

  const declineTeamInvitation = useEvent(
    async (teamInvitation: TeamInvitation, options?: MutationOptions) => {
      const { teamID, teamMemberID } = extractUuidsFromInvitationAcceptPath(
        teamInvitation.acceptInvitationPath
      )
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: options?.suppressToasts
      })

      const result = await callMutation(apolloMutate, {
        variables: { teamID, teamMemberID },
        transform: data => {
          const declined = data?.declineTeamInvitation
          if (!declined) {
            throw new Error(
              t("Request.TeamInvitation.DeclineTeamInvitation.Failure")
            )
          }
          return declined
        },
        apolloOptions: {
          update: cache => {
            cache.updateFragment<TeamInvitationFragment>(
              {
                id: cache.identify({
                  id: teamInvitation.id,
                  __typename: TeamInvitationTypename
                }),
                fragment: TeamInvitationFragmentDoc,
                fragmentName: "TeamInvitation"
              },
              data => (data ? { ...data, invitationRejected: true } : null)
            )
          },
          refetchQueries: [
            {
              query: TeamInvitationsDocument,
              variables: { pending: true }
            }
          ],
          onCompleted: () => {
            raiseSuccessToast()
          },
          onError: () => {
            raiseErrorToast()
          }
        }
      })
      fetchProjects()
      return result
    }
  )

  return { declineTeamInvitation }
}
